import { useCallback, useEffect, useRef, useState } from 'react';
import ProductCard from './ProductCard';
import {
  cardFromScroll,
  nextPage,
  pageCount,
  pageFromScroll,
  scrollLeftForCard,
  scrollLeftForPage,
} from '../../utils/carousel';
import {
  idle,
  isDragging,
  isPressed,
  move,
  press,
  release,
  scrollLeftDuring,
} from '../../utils/dragScroll';
import './RelatedProducts.css';

const AUTO_ADVANCE_MS = 4000;
// Long enough to outlast the settle scroll a released drag starts.
const SNAP_RESTORE_MS = 500;
// The click a drag produces arrives immediately after the release, so a short
// window catches it without ever reaching a later, genuine click.
const CLICK_GUARD_MS = 300;

const RelatedProducts = ({ products, title = 'Related Products' }) => {
  const trackRef = useRef(null);
  // How many cards fit a page is a CSS decision, so the geometry is measured
  // off the laid-out track rather than hard-coded per breakpoint. It is cached
  // here because only a resize can change it, and reading it back on every
  // scroll frame would force a style recalc mid-animation.
  const geometryRef = useRef({
    scrollWidth: 0,
    clientWidth: 0,
    itemWidth: 0,
    gap: 0,
  });
  // The gesture lives in a ref because the pointer handlers act on it
  // imperatively; `phase` mirrors it purely so rendering and effects can react.
  const gestureRef = useRef(idle());

  const clickGuardRef = useRef(false);
  const clickGuardTimerRef = useRef(null);

  const [phase, setPhase] = useState('idle');
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const hasProducts = Array.isArray(products) && products.length > 0;
  const dragging = phase === 'dragging';
  const pressed = phase !== 'idle';

  const applyGesture = useCallback(next => {
    // `move` hands back the very same state when nothing changed, so this
    // skips a render on every mouse move within a drag.
    if (next === gestureRef.current) return;

    gestureRef.current = next;
    setPhase(next.phase);
  }, []);

  const syncPage = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    setPage(
      pageFromScroll({ ...geometryRef.current, scrollLeft: track.scrollLeft })
    );
  }, []);

  // The dot count and the active dot come off the same measurement, so they
  // cannot disagree after a resize.
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    geometryRef.current = {
      scrollWidth: track.scrollWidth,
      clientWidth: track.clientWidth,
      // Measured rather than derived: how wide a card is, and so how many fit
      // a page, is settled entirely in CSS. The rect width keeps the fraction
      // that offsetWidth would round away and drift on over a long row.
      itemWidth: track.firstElementChild
        ? track.firstElementChild.getBoundingClientRect().width
        : 0,
      gap: parseFloat(window.getComputedStyle(track).columnGap) || 0,
    };

    setPages(pageCount(geometryRef.current));
    syncPage();
  }, [syncPage]);

  const scrollToOffset = useCallback(
    left => {
      const track = trackRef.current;
      if (!track) return;

      track.scrollTo({
        left,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    },
    [prefersReducedMotion]
  );

  const scrollToPage = useCallback(
    target => scrollToOffset(scrollLeftForPage(target, geometryRef.current)),
    [scrollToOffset]
  );

  const armClickGuard = useCallback(() => {
    clickGuardRef.current = true;
    window.clearTimeout(clickGuardTimerRef.current);

    // Bounded, so a click that never arrives - the release landed outside the
    // row, or outside the window - cannot leave a trap set for a later,
    // genuine click, a keyboard Enter on a card included.
    clickGuardTimerRef.current = window.setTimeout(() => {
      clickGuardRef.current = false;
    }, CLICK_GUARD_MS);
  }, []);

  const beginDrag = useCallback(
    event => {
      const track = trackRef.current;
      if (!track) return;

      const pointer = {
        pointerType: event.pointerType,
        button: event.button,
        clientX: event.clientX,
        canScroll: pages > 1,
      };

      // Whether a gesture starts does not depend on the offset, so this
      // settles it before anything touches the row.
      if (!isPressed(press({ ...pointer, scrollLeft: 0 }))) return;

      // Snapping goes first. Aborting the scroll below with it still live
      // would re-snap the row a frame later, leaving the offset captured here
      // stale by up to half a card. The effect below owns restoring it.
      track.style.scrollSnapType = 'none';
      // A dot's or the auto-advance's smooth scroll may still be running, and
      // left alone it would fight the drag.
      track.scrollTo({ left: track.scrollLeft, behavior: 'auto' });

      applyGesture(press({ ...pointer, scrollLeft: track.scrollLeft }));
    },
    [applyGesture, pages]
  );

  const endDrag = useCallback(() => {
    const wasDragging = isDragging(gestureRef.current);
    applyGesture(release());

    const track = trackRef.current;
    if (!wasDragging || !track) return;

    // A press that turned into a drag must not also click through to the card
    // it happens to finish over. Armed from the ref rather than from rendered
    // state, which a fast enough flick can skip past entirely.
    armClickGuard();

    // Settle on the nearest card, not the nearest page. Rounding to a page
    // boundary threw away any drag shorter than half a page, so nudging the
    // row along by one card was impossible - it always sprang back. This also
    // matches where CSS puts its snap points, so a drag now lands exactly
    // where a wheel or a swipe would.
    scrollToOffset(
      scrollLeftForCard(
        cardFromScroll({
          ...geometryRef.current,
          scrollLeft: track.scrollLeft,
        }),
        geometryRef.current
      )
    );
  }, [applyGesture, armClickGuard, scrollToOffset]);

  // A card is an anchor wrapping an image, so both are natively draggable. Left
  // alone the browser starts its own drag-and-drop and fires pointercancel,
  // which ends the gesture before it moves a pixel - cancelling it at dragstart
  // is already too late, because pointercancel has fired by then. Preventing
  // the default here, before the browser commits to a drag, is what keeps the
  // row grabbable by its images.
  const suppressNativeDrag = useCallback(event => {
    if (isPressed(gestureRef.current)) event.preventDefault();
  }, []);

  const onDragMove = useCallback(
    event => {
      const previous = gestureRef.current;
      const next = move(previous, {
        clientX: event.clientX,
        buttons: event.buttons,
      });

      // The button came up somewhere this page never saw: off-window, or over
      // browser chrome. Finish exactly as a real release would, so the row
      // still settles on a page instead of being left mid-card.
      if (isPressed(previous) && !isPressed(next)) {
        endDrag();
        return;
      }

      applyGesture(next);

      const track = trackRef.current;
      if (!track || !isDragging(next)) return;

      track.scrollLeft = scrollLeftDuring(next, event.clientX);
    },
    [applyGesture, endDrag]
  );

  // The gesture is tracked on the window so a release outside the track still
  // ends it. Nothing captures the pointer: capturing would retarget the click
  // away from the card's own link and break every plain click.
  useEffect(() => {
    if (!pressed) return undefined;

    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    // A button released over another window never reaches this page; losing
    // focus is the only signal that arrives.
    window.addEventListener('blur', endDrag);

    return () => {
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      window.removeEventListener('blur', endDrag);
    };
  }, [pressed, onDragMove, endDrag]);

  // Mandatory snapping fights every imperative scrollLeft write, so it is
  // lifted for the length of a drag. Deriving it from the phase means every
  // path out of a drag restores it: no caller has to remember to.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    if (pressed) {
      track.style.scrollSnapType = 'none';
      return undefined;
    }

    // Every path out of a gesture lands here, so snapping can never be left
    // off: the restore is scheduled by the transition itself rather than by
    // each caller remembering to schedule it. A release settles by at most
    // half a page, which this comfortably outlasts.
    const id = window.setTimeout(() => {
      track.style.scrollSnapType = '';
    }, SNAP_RESTORE_MS);

    return () => window.clearTimeout(id);
  }, [pressed]);

  useEffect(() => () => window.clearTimeout(clickGuardTimerRef.current), []);

  const onClickCapture = useCallback(event => {
    if (!clickGuardRef.current) return;

    // The drag's own click has arrived; disarm at once so a genuine click a
    // moment later still lands.
    clickGuardRef.current = false;
    window.clearTimeout(clickGuardTimerRef.current);
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [measure, products]);

  useEffect(() => {
    const onVisibilityChange = () => setIsTabHidden(document.hidden);

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setPrefersReducedMotion(query.matches);

    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Auto-scroll holds off while someone is reading or driving the row, and
  // stops outright for a reduced-motion preference or a backgrounded tab.
  const isPaused =
    pages <= 1 ||
    isHovered ||
    isFocused ||
    isTouching ||
    pressed ||
    isTabHidden ||
    prefersReducedMotion;

  useEffect(() => {
    if (isPaused) return undefined;

    const id = setInterval(() => {
      const track = trackRef.current;
      if (!track) return;

      // Read the live scroll position rather than trusting `page`, so a tick
      // that lands just after a drag still advances from where the row is.
      const current = pageFromScroll({
        ...geometryRef.current,
        scrollLeft: track.scrollLeft,
      });
      scrollToPage(nextPage(current, pageCount(geometryRef.current)));
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(id);
  }, [isPaused, scrollToPage]);

  if (!hasProducts) return null;

  const trackClassName = [
    'related-products-track',
    pages > 1 ? 'is-draggable' : '',
    dragging ? 'is-dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      className="related-products"
      aria-labelledby="related-products-title"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <h2 className="related-products-title" id="related-products-title">
        {title}
      </h2>

      <div
        className={trackClassName}
        ref={trackRef}
        onScroll={syncPage}
        onPointerDown={beginDrag}
        onMouseDown={suppressNativeDrag}
        onClickCapture={onClickCapture}
        // Card images and links are natively draggable, which would hand the
        // gesture to the browser's drag-and-drop instead of the row.
        onDragStart={event => event.preventDefault()}
        onTouchStart={() => setIsTouching(true)}
        onTouchEnd={() => setIsTouching(false)}
        onTouchCancel={() => setIsTouching(false)}
      >
        {products.map(product => (
          <div className="related-products-item" key={product.id}>
            <ProductCard product={product} />
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="related-products-dots">
          {Array.from({ length: pages }, (unused, index) => (
            <button
              type="button"
              key={index}
              className="related-products-dot"
              aria-current={index === page}
              aria-label={`Go to related products page ${index + 1} of ${pages}`}
              onClick={() => scrollToPage(index)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default RelatedProducts;
